import { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  format, startOfWeek, addWeeks, subWeeks, addDays,
  eachDayOfInterval, isSameDay, parseISO, isToday,
  getHours, getMinutes, addMinutes,
} from 'date-fns';
import {
  ChevronLeftIcon, ChevronRightIcon, PlusIcon,
  ArrowDownTrayIcon, VideoCameraIcon, EllipsisHorizontalIcon,
  ViewColumnsIcon, UserIcon, CheckIcon, XMarkIcon, ClockIcon,
} from '@heroicons/react/24/outline';
import { Menu } from '@headlessui/react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { getExternalCalendarEvents } from '../services/calendarSubscriptionService';
import {
  getAppointments, createAppointment, updateAppointment, deleteAppointment,
  cancelRecurringAppointments, joinTelehealthRoom, getPractitioners,
  updateGroupClientStatus, generateGroupInvoices,
  approveAppointment, declineAppointment,
} from '../services/appointmentService';
import Modal from '../components/common/Modal';
import Drawer from '../components/common/Drawer';
import Spinner from '../components/common/Spinner';
import AppointmentForm from '../components/features/appointments/AppointmentForm';
import TelehealthRoom from '../components/features/telehealth/TelehealthRoom';

// ─── Calendar constants ────────────────────────────────────────────────────────
const DENSITY_KEY = 'calendar_density';
const HOUR_HEIGHT_DEFAULT = 64;
const HOUR_HEIGHT_COMPACT = 48;
const START_HOUR = 7;   // 7 AM
const END_HOUR = 22;    // 10 PM (exclusive)
const TOTAL_HOURS = END_HOUR - START_HOUR;
const HOURS = Array.from({ length: TOTAL_HOURS }, (_, i) => i + START_HOUR);

// Distinct colors assigned to practitioners by position in the list
const PRACTITIONER_PALETTE = [
  '#6366f1', // indigo
  '#f59e0b', // amber
  '#10b981', // emerald
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#f97316', // orange
  '#84cc16', // lime
];

// ─── Helpers ───────────────────────────────────────────────────────────────────
function timeToTop(date, hourHeight) {
  const h = getHours(date);
  const m = getMinutes(date);
  return Math.max(0, ((h - START_HOUR) * 60 + m) / 60 * hourHeight);
}

function durationToHeight(minutes, hourHeight) {
  return Math.max((minutes / 60) * hourHeight, 20);
}

/** Greedy column packing — returns Map<id, { left, width }> (0–1 fractions) */
function computeAppointmentLayout(dayAppointments) {
  const sorted = [...dayAppointments].sort(
    (a, b) => parseISO(a.startTime) - parseISO(b.startTime),
  );
  const columns = []; // endTime of last appt placed in each column
  const placement = new Map();

  for (const appt of sorted) {
    const start = parseISO(appt.startTime);
    const end = addMinutes(start, appt.durationMinutes);
    let col = columns.findIndex((endTime) => endTime <= start);
    if (col === -1) { col = columns.length; columns.push(end); }
    else columns[col] = end;
    placement.set(appt.id, col);
  }

  const numCols = Math.max(columns.length, 1);
  const result = new Map();
  for (const [id, col] of placement) {
    result.set(id, { left: col / numCols, width: 1 / numCols });
  }
  return result;
}

function getDays(view, anchor) {
  if (view === 'day') return [anchor];
  if (view === 'workWeek') {
    const mon = startOfWeek(anchor, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: mon, end: addDays(mon, 4) });
  }
  const sun = startOfWeek(anchor, { weekStartsOn: 0 });
  return eachDayOfInterval({ start: sun, end: addDays(sun, 6) });
}

function navigate(view, anchor, direction) {
  const delta = direction === 'prev' ? -1 : 1;
  if (view === 'day') return addDays(anchor, delta);
  return delta > 0 ? addWeeks(anchor, 1) : subWeeks(anchor, 1);
}

function formatRangeLabel(view, anchor) {
  const days = getDays(view, anchor);
  if (view === 'day') return format(anchor, 'EEEE, MMMM d, yyyy');
  const first = days[0];
  const last = days[days.length - 1];
  if (format(first, 'MMMyyyy') === format(last, 'MMMyyyy')) {
    return `${format(first, 'MMM d')} – ${format(last, 'd, yyyy')}`;
  }
  if (format(first, 'yyyy') === format(last, 'yyyy')) {
    return `${format(first, 'MMM d')} – ${format(last, 'MMM d, yyyy')}`;
  }
  return `${format(first, 'MMM d, yyyy')} – ${format(last, 'MMM d, yyyy')}`;
}

function practitionerInitials(practitioner) {
  const first = practitioner?.user?.firstName?.[0] ?? '';
  const last = practitioner?.user?.lastName?.[0] ?? '';
  return (first + last).toUpperCase() || '?';
}

// ─── AppointmentBlock ──────────────────────────────────────────────────────────
function AppointmentBlock({ appointment, layout, onEdit, onJoin, hourHeight, practitionerColor, showPractitioner }) {
  const start = parseISO(appointment.startTime);
  const top = timeToTop(start, hourHeight);
  const height = durationToHeight(appointment.durationMinutes, hourHeight);
  const typeColor = appointment.status === 'pending_approval' ? '#f59e0b' : (appointment.appointmentType?.color || '#6366f1');
  const borderColor = showPractitioner && practitionerColor ? practitionerColor : typeColor;
  const compact = height < 36;
  const isCancelled = appointment.status === 'cancelled';
  const isNoShow = appointment.status === 'no_show';
  const isGroup = appointment.isGroup;

  const groupClients = appointment.groupClients ?? [];
  const activeGroupClients = groupClients.filter((gc) => gc.status !== 'cancelled');
  const groupLabel = isGroup
    ? activeGroupClients.length <= 2
      ? activeGroupClients.map((gc) => `${gc.client?.firstName ?? '?'}`).join(', ') || 'Group'
      : `Group (${activeGroupClients.length})`
    : null;

  const isPendingApproval = appointment.status === 'pending_approval';

  const statusStyle = {};
  if (isNoShow) {
    statusStyle.backgroundImage =
      `repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.04) 4px, rgba(0,0,0,0.04) 8px)`;
  }
  if (isPendingApproval) {
    statusStyle.backgroundImage =
      `repeating-linear-gradient(45deg, transparent, transparent 6px, rgba(245,158,11,0.08) 6px, rgba(245,158,11,0.08) 12px)`;
  }

  const clientLabel = isGroup
    ? groupLabel
    : appointment.client
      ? `${appointment.client.firstName} ${appointment.client.lastName}`
      : 'Unknown';

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${clientLabel} at ${format(start, 'h:mm a')}`}
      style={{
        position: 'absolute',
        top: `${top}px`,
        height: `${height}px`,
        left: `calc(${layout.left * 100}% + 2px)`,
        width: `calc(${layout.width * 100}% - 4px)`,
        borderLeft: `3px solid ${borderColor}`,
        backgroundColor: `${typeColor}18`,
        zIndex: 10,
        ...statusStyle,
      }}
      className={`rounded overflow-hidden cursor-pointer hover:brightness-95 transition-all select-none
        ${isCancelled ? 'opacity-50' : ''}`}
      onClick={() => onEdit(appointment)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onEdit(appointment)}
    >
      <div className="px-1.5 py-0.5 h-full flex flex-col justify-start overflow-hidden relative">
        {/* Badges: pending / group / telehealth */}
        {!compact && (
          <div className="absolute top-0.5 right-0.5 flex items-center gap-0.5">
            {isPendingApproval && (
              <span className="text-[9px] font-bold px-1 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                REQ
              </span>
            )}
            {isGroup && (
              <span className="text-[9px] font-bold px-1 rounded" style={{ backgroundColor: `${typeColor}30`, color: typeColor }}>
                GRP
              </span>
            )}
            {appointment.isTelehealth && <VideoCameraIcon className="h-3 w-3" style={{ color: typeColor }} />}
          </div>
        )}

        {/* Practitioner avatar badge */}
        {showPractitioner && practitionerColor && !compact && (
          <div
            className="absolute bottom-0.5 right-0.5 w-4 h-4 rounded-full flex items-center justify-center text-white"
            style={{ backgroundColor: practitionerColor, fontSize: '8px', fontWeight: 700, lineHeight: 1 }}
            title={`${appointment.practitioner?.user?.firstName ?? ''} ${appointment.practitioner?.user?.lastName ?? ''}`.trim()}
          >
            {practitionerInitials(appointment.practitioner)}
          </div>
        )}

        <p
          className={`text-xs font-semibold leading-tight truncate ${isCancelled ? 'line-through' : ''}`}
          style={{ color: typeColor }}
        >
          {clientLabel}
        </p>
        {!compact && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate leading-tight">
            {format(start, 'h:mm a')}
            {appointment.appointmentType && ` · ${appointment.appointmentType.name}`}
          </p>
        )}
        {!compact && showPractitioner && appointment.practitioner?.user && (
          <p className="text-xs truncate leading-tight" style={{ color: practitionerColor ?? typeColor }}>
            {appointment.practitioner.user.firstName} {appointment.practitioner.user.lastName}
          </p>
        )}
        {!compact && appointment.isTelehealth && appointment.telehealthLink && (
          <button
            onClick={(e) => { e.stopPropagation(); onJoin(appointment); }}
            className="mt-0.5 flex items-center gap-0.5 text-xs text-blue-600 hover:underline w-fit"
          >
            <VideoCameraIcon className="h-3 w-3" />
            Join
          </button>
        )}
      </div>
    </div>
  );
}

// ─── ExternalEventBlock ────────────────────────────────────────────────────────
function ExternalEventBlock({ event, hourHeight }) {
  const start = new Date(event.start);
  const end = new Date(event.end);
  const durationMins = (end - start) / 60000;
  const top = timeToTop(start, hourHeight);
  const height = durationToHeight(Math.max(durationMins, 15), hourHeight);
  const compact = height < 36;

  return (
    <div
      title={`${event.title}\n${event.subscriptionName}${event.location ? `\n📍 ${event.location}` : ''}`}
      style={{
        position: 'absolute',
        top: `${top}px`,
        height: `${height}px`,
        left: '2px',
        right: '2px',
        borderLeft: `3px dashed ${event.color}`,
        backgroundColor: `${event.color}12`,
        zIndex: 5,
      }}
      className="rounded overflow-hidden select-none cursor-default"
    >
      <div className="px-1.5 py-0.5 h-full flex flex-col justify-start overflow-hidden">
        <p className="text-xs font-medium leading-tight truncate" style={{ color: event.color }}>
          {event.title}
        </p>
        {!compact && (
          <p className="text-xs leading-tight truncate" style={{ color: `${event.color}99` }}>
            {format(start, 'h:mm a')} · {event.subscriptionName}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── DayColumn ─────────────────────────────────────────────────────────────────
function DayColumn({ day, appointments, externalEvents = [], onEdit, onJoin, onSlotClick, isEven, hourHeight, practitionerColorMap, showPractitioner }) {
  const TOTAL_HEIGHT = TOTAL_HOURS * hourHeight;
  const layout = computeAppointmentLayout(appointments);
  const now = new Date();
  const showNowLine = isToday(day)
    && getHours(now) >= START_HOUR
    && getHours(now) < END_HOUR;
  const nowTop = showNowLine ? timeToTop(now, hourHeight) : 0;
  const altBg = isEven
    ? 'bg-white dark:bg-neutral-800'
    : 'bg-neutral-50 dark:bg-neutral-700/40';

  return (
    <div className={`relative flex-1 border-l border-neutral-200 dark:border-neutral-700 ${altBg}`} style={{ height: `${TOTAL_HEIGHT}px` }}>
      {/* Hour lines */}
      {HOURS.map((h) => (
        <div
          key={h}
          className="absolute w-full border-t border-neutral-100 dark:border-neutral-700/60"
          style={{ top: `${(h - START_HOUR) * hourHeight}px` }}
        />
      ))}
      {/* Half-hour lines */}
      {HOURS.map((h) => (
        <div
          key={`${h}h`}
          className="absolute w-full border-t border-dashed border-neutral-100 dark:border-neutral-700/30"
          style={{ top: `${(h - START_HOUR) * hourHeight + hourHeight / 2}px` }}
        />
      ))}

      {/* Clickable hour slots */}
      {HOURS.map((h) => (
        <div
          key={`slot-${h}`}
          className="absolute w-full hover:bg-primary-50/60 dark:hover:bg-primary-900/10 transition-colors cursor-pointer"
          style={{ top: `${(h - START_HOUR) * hourHeight}px`, height: `${hourHeight}px` }}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const frac = (e.clientY - rect.top) / hourHeight;
            const mins = Math.floor(frac * 4) * 15; // snap to 15min
            onSlotClick(day, h, mins);
          }}
        />
      ))}

      {/* Now indicator */}
      {showNowLine && (
        <div
          className="absolute w-full z-20 pointer-events-none flex items-center"
          style={{ top: `${nowTop}px` }}
        >
          <div className="w-2 h-2 rounded-full bg-danger-500 shrink-0 -ml-1" />
          <div className="flex-1 h-px bg-danger-500" />
        </div>
      )}

      {/* External calendar events (read-only) */}
      {externalEvents.map((ev) => (
        <ExternalEventBlock key={ev.id} event={ev} hourHeight={hourHeight} />
      ))}

      {/* Appointment blocks */}
      {appointments.map((appt) => (
        <AppointmentBlock
          key={appt.id}
          appointment={appt}
          layout={layout.get(appt.id) || { left: 0, width: 1 }}
          onEdit={onEdit}
          onJoin={onJoin}
          hourHeight={hourHeight}
          practitionerColor={practitionerColorMap?.get(appt.practitionerId)}
          showPractitioner={showPractitioner}
        />
      ))}
    </div>
  );
}

// ─── CalendarGrid ──────────────────────────────────────────────────────────────
function CalendarGrid({ days, appointments, externalEvents = [], onEdit, onJoin, onSlotClick, hourHeight, practitionerColorMap, showPractitioner }) {
  const TOTAL_HEIGHT = TOTAL_HOURS * hourHeight;
  const scrollRef = useRef(null);

  // Scroll to current time (or 8 AM) on mount
  useEffect(() => {
    if (!scrollRef.current) return;
    const now = new Date();
    const targetHour = (getHours(now) >= START_HOUR && getHours(now) < END_HOUR)
      ? getHours(now) - 1
      : 8;
    const scrollTo = Math.max(0, (targetHour - START_HOUR) * hourHeight - 32);
    scrollRef.current.scrollTop = scrollTo;
  }, [hourHeight]);

  const appointmentsForDay = (day) =>
    appointments.filter((a) => isSameDay(parseISO(a.startTime), day));

  const externalEventsForDay = (day) =>
    externalEvents.filter((e) => isSameDay(new Date(e.start), day));

  return (
    <div className="flex flex-col flex-1 overflow-hidden border border-neutral-200 dark:border-neutral-700 rounded-xl bg-white dark:bg-neutral-800">
      {/* Day header row */}
      <div className="flex border-b border-neutral-200 dark:border-neutral-700 shrink-0">
        {/* Time gutter header */}
        <div className="w-16 shrink-0" />
        {days.map((day, i) => {
          const today = isToday(day);
          const altBg = i % 2 === 0 ? 'bg-white dark:bg-neutral-800' : 'bg-neutral-50 dark:bg-neutral-700/40';
          return (
            <div
              key={day.toISOString()}
              className={`flex-1 border-l border-neutral-200 dark:border-neutral-700 py-2 text-center ${altBg}`}
            >
              <p className={`text-xs font-medium uppercase tracking-wide ${today ? 'text-primary-600' : 'text-neutral-400 dark:text-neutral-500'}`}>
                {format(day, 'EEE')}
              </p>
              <div className={`mx-auto mt-0.5 w-8 h-8 flex items-center justify-center rounded-full text-sm font-semibold ${
                today
                  ? 'bg-primary-600 text-white'
                  : 'text-neutral-700 dark:text-neutral-200'
              }`}>
                {format(day, 'd')}
              </div>
            </div>
          );
        })}
      </div>

      {/* Scrollable time grid */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="flex" style={{ height: `${TOTAL_HEIGHT}px` }}>
          {/* Time labels gutter */}
          <div className="w-16 shrink-0 relative bg-white dark:bg-neutral-800" style={{ height: `${TOTAL_HEIGHT}px` }}>
            {HOURS.slice(1).map((h) => (
              <div
                key={h}
                className="absolute text-xs text-neutral-400 dark:text-neutral-500 leading-none text-right"
                style={{ top: `${(h - START_HOUR) * hourHeight - 8}px`, right: 0, width: '60px' }}
              >
                {format(new Date(2000, 0, 1, h), 'ha').toLowerCase()}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((day, i) => (
            <DayColumn
              key={day.toISOString()}
              day={day}
              isEven={i % 2 === 0}
              appointments={appointmentsForDay(day)}
              externalEvents={externalEventsForDay(day)}
              onEdit={onEdit}
              onJoin={onJoin}
              onSlotClick={onSlotClick}
              hourHeight={hourHeight}
              practitionerColorMap={practitionerColorMap}
              showPractitioner={showPractitioner}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Provider legend pill ──────────────────────────────────────────────────────
function ProviderLegend({ practitioners, practitionerColorMap, selectedId, onSelect }) {
  if (practitioners.length <= 1) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <button
        onClick={() => onSelect(null)}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
          !selectedId
            ? 'bg-neutral-800 dark:bg-neutral-200 text-white dark:text-neutral-900 border-transparent'
            : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700'
        }`}
      >
        <UserIcon className="h-3 w-3" />
        All providers
      </button>
      {practitioners.map((p) => {
        const color = practitionerColorMap.get(p.id);
        const name = `${p.user?.firstName ?? ''} ${p.user?.lastName ?? ''}`.trim();
        const isSelected = selectedId === p.id;
        return (
          <button
            key={p.id}
            onClick={() => onSelect(isSelected ? null : p.id)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              isSelected
                ? 'text-white border-transparent'
                : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700'
            }`}
            style={isSelected ? { backgroundColor: color, borderColor: color } : { '--dot-color': color }}
          >
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: color }}
            />
            {name || 'Unknown'}
          </button>
        );
      })}
    </div>
  );
}

// ─── GroupMembersPanel ─────────────────────────────────────────────────────────
const GROUP_CLIENT_STATUSES = ['enrolled', 'attended', 'no_show', 'cancelled'];
const STATUS_LABELS = { enrolled: 'Enrolled', attended: 'Attended', no_show: 'No show', cancelled: 'Cancelled' };
const STATUS_COLORS = {
  enrolled: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  attended: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  no_show: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  cancelled: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400',
};

function GroupMembersPanel({ appointment, onRefresh }) {
  const queryClient = useQueryClient();
  const groupClients = appointment.groupClients ?? [];
  const enrolledCount = groupClients.filter((gc) => gc.status === 'enrolled' || gc.status === 'attended').length;

  const statusMutation = useMutation({
    mutationFn: ({ clientId, status }) => updateGroupClientStatus(appointment.id, clientId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      if (onRefresh) onRefresh();
    },
    onError: () => toast.error('Failed to update status'),
  });

  const invoiceMutation = useMutation({
    mutationFn: () => generateGroupInvoices(appointment.id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success(data.message || 'Invoices generated');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to generate invoices'),
  });

  return (
    <div className="border-t border-neutral-200 dark:border-neutral-700 pt-5 mt-2 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
          Group members ({groupClients.length}
          {appointment.maxCapacity ? ` / ${appointment.maxCapacity}` : ''})
        </h3>
        <button
          onClick={() => invoiceMutation.mutate()}
          disabled={invoiceMutation.isPending || enrolledCount === 0}
          className="btn btn-secondary btn-sm disabled:opacity-60"
        >
          {invoiceMutation.isPending ? 'Generating…' : `Generate ${enrolledCount} invoice${enrolledCount !== 1 ? 's' : ''}`}
        </button>
      </div>

      {groupClients.length === 0 ? (
        <p className="text-sm text-neutral-400">No clients enrolled.</p>
      ) : (
        <div className="space-y-2">
          {groupClients.map((gc) => (
            <div key={gc.clientId} className="flex items-center justify-between gap-2">
              <span className="text-sm text-neutral-800 dark:text-neutral-200 truncate flex-1">
                {gc.client?.firstName ?? '?'} {gc.client?.lastName ?? ''}
              </span>
              <select
                value={gc.status}
                onChange={(e) => statusMutation.mutate({ clientId: gc.clientId, status: e.target.value })}
                className={`text-xs font-medium px-2 py-1 rounded border-0 cursor-pointer focus:ring-1 focus:ring-primary-500 ${STATUS_COLORS[gc.status]}`}
              >
                {GROUP_CLIENT_STATUSES.map((s) => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── RequestsPanel ─────────────────────────────────────────────────────────────
function RequestsPanel({ requests, onApprove, onDecline, approvingId, decliningId }) {
  if (requests.length === 0) return null;

  return (
    <div className="mb-4 shrink-0 rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-amber-200 dark:border-amber-700">
        <ClockIcon className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">
          Appointment requests ({requests.length})
        </span>
      </div>
      <ul className="divide-y divide-amber-100 dark:divide-amber-800">
        {requests.map((appt) => {
          const clientName = appt.client
            ? `${appt.client.firstName} ${appt.client.lastName}`
            : 'Unknown client';
          const typeName = appt.appointmentType?.name ?? 'Appointment';
          const start = parseISO(appt.startTime);
          return (
            <li key={appt.id} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                  {clientName}
                </p>
                <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5">
                  {typeName} · {format(start, 'EEE, MMM d')} at {format(start, 'h:mm a')}
                  {appt.durationMinutes && ` · ${appt.durationMinutes} min`}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => onDecline(appt)}
                  disabled={decliningId === appt.id || approvingId === appt.id}
                  className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-danger-200 dark:border-danger-700 text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/30 disabled:opacity-50 transition-colors"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                  Decline
                </button>
                <button
                  onClick={() => onApprove(appt)}
                  disabled={approvingId === appt.id || decliningId === appt.id}
                  className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-green-200 dark:border-green-700 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 disabled:opacity-50 transition-colors"
                >
                  <CheckIcon className="h-3.5 w-3.5" />
                  {approvingId === appt.id ? 'Approving…' : 'Approve'}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function Appointments() {
  const queryClient = useQueryClient();
  const [view, setView] = useState('workWeek');
  const [anchor, setAnchor] = useState(new Date());
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createDefaults, setCreateDefaults] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [telehealthUrl, setTelehealthUrl] = useState(null);
  const [telehealthLoading, setTelehealthLoading] = useState(false);
  const [selectedPractitionerId, setSelectedPractitionerId] = useState(null);
  const [density, setDensity] = useState(
    () => localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'default'
  );
  const [approvingId, setApprovingId] = useState(null);
  const [decliningId, setDecliningId] = useState(null);
  const hourHeight = density === 'compact' ? HOUR_HEIGHT_COMPACT : HOUR_HEIGHT_DEFAULT;

  const toggleDensity = () => {
    const next = density === 'compact' ? 'default' : 'compact';
    setDensity(next);
    localStorage.setItem(DENSITY_KEY, next);
  };

  const days = getDays(view, anchor);
  const rangeStart = days[0];
  const rangeEnd = days[days.length - 1];

  const { data: practitionersData } = useQuery({
    queryKey: ['practitioners'],
    queryFn: getPractitioners,
    staleTime: 5 * 60 * 1000,
  });
  const practitioners = useMemo(() => practitionersData ?? [], [practitionersData]);

  // Assign a stable color to each practitioner by their position in the sorted list
  const practitionerColorMap = useMemo(() => {
    const map = new Map();
    practitioners.forEach((p, i) => {
      map.set(p.id, PRACTITIONER_PALETTE[i % PRACTITIONER_PALETTE.length]);
    });
    return map;
  }, [practitioners]);

  const showPractitioner = !selectedPractitionerId && practitioners.length > 1;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['appointments', {
      dateFrom: format(rangeStart, 'yyyy-MM-dd'),
      dateTo: format(rangeEnd, 'yyyy-MM-dd'),
      practitionerId: selectedPractitionerId ?? undefined,
    }],
    queryFn: () =>
      getAppointments({
        dateFrom: format(rangeStart, 'yyyy-MM-dd'),
        dateTo: format(addDays(rangeEnd, 1), 'yyyy-MM-dd'),
        limit: 200,
        ...(selectedPractitionerId ? { practitionerId: selectedPractitionerId } : {}),
      }),
  });

  // Fetch all pending approval requests regardless of the current calendar view window
  const { data: requestsData } = useQuery({
    queryKey: ['appointments', { status: 'pending_approval' }],
    queryFn: () => getAppointments({ status: 'pending_approval', limit: 100 }),
    refetchInterval: 30_000,
  });
  const pendingRequests = requestsData?.appointments ?? [];

  const { data: externalEvents = [] } = useQuery({
    queryKey: ['externalCalendarEvents', format(rangeStart, 'yyyy-MM-dd'), format(rangeEnd, 'yyyy-MM-dd')],
    queryFn: () => getExternalCalendarEvents(
      rangeStart.toISOString(),
      addDays(rangeEnd, 1).toISOString(),
    ),
    staleTime: 10 * 60 * 1000, // 10 min client-side cache
    retry: false, // Don't retry — external calendars may be flaky
  });

  const createMutation = useMutation({
    mutationFn: createAppointment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      setIsCreateOpen(false);
      setCreateDefaults(null);
      toast.success('Appointment booked');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to create appointment'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data: formData }) => updateAppointment(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      setEditingAppointment(null);
      toast.success('Appointment updated');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update appointment'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAppointment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      setDeleteTarget(null);
      toast.success('Appointment deleted');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete appointment'),
  });

  const cancelSeriesMutation = useMutation({
    mutationFn: ({ id }) => cancelRecurringAppointments(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      setEditingAppointment(null);
      toast.success(res.message || 'Series cancelled');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to cancel series'),
  });

  const handleJoinTelehealth = async (appointment) => {
    setTelehealthLoading(true);
    try {
      const { url } = await joinTelehealthRoom(appointment.id);
      setTelehealthUrl(url);
    } catch {
      if (appointment.telehealthLink) setTelehealthUrl(appointment.telehealthLink);
      else toast.error('Failed to get telehealth join link');
    } finally {
      setTelehealthLoading(false);
    }
  };

  const handleApprove = async (appt) => {
    setApprovingId(appt.id);
    try {
      await approveAppointment(appt.id);
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Appointment approved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve appointment');
    } finally {
      setApprovingId(null);
    }
  };

  const handleDecline = async (appt) => {
    setDecliningId(appt.id);
    try {
      await declineAppointment(appt.id);
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Appointment request declined');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to decline appointment');
    } finally {
      setDecliningId(null);
    }
  };

  const handleSlotClick = (day, hour, extraMinutes = 0) => {
    const d = new Date(day);
    d.setHours(hour, extraMinutes, 0, 0);
    setCreateDefaults({
      date: format(d, 'yyyy-MM-dd'),
      time: format(d, 'HH:mm'),
    });
    setIsCreateOpen(true);
  };

  const appointments = data?.appointments ?? [];

  const editDefaultValues = editingAppointment
    ? {
        isGroup: editingAppointment.isGroup ?? false,
        clientId: editingAppointment.clientId ?? '',
        clientIds: (editingAppointment.groupClients ?? []).map((gc) => gc.clientId),
        maxCapacity: editingAppointment.maxCapacity ?? '',
        appointmentTypeId: editingAppointment.appointmentTypeId ?? '',
        date: format(parseISO(editingAppointment.startTime), 'yyyy-MM-dd'),
        time: format(parseISO(editingAppointment.startTime), 'HH:mm'),
        durationMinutes: editingAppointment.durationMinutes,
        isTelehealth: editingAppointment.isTelehealth,
        location: editingAppointment.location ?? '',
        telehealthLink: editingAppointment.telehealthLink ?? '',
        notes: editingAppointment.notes ?? '',
        status: editingAppointment.status,
        cancellationReason: editingAppointment.cancellationReason ?? '',
      }
    : {};

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-4 shrink-0">
        <h1>Appointments</h1>
        <div className="flex items-center gap-2">
          {/* Export dropdown */}
          <Menu as="div" className="relative">
            <Menu.Button className="btn btn-secondary btn-icon" aria-label="More options">
              <EllipsisHorizontalIcon className="h-5 w-5" />
            </Menu.Button>
            <Menu.Items className="absolute right-0 mt-1 w-44 rounded-xl bg-white dark:bg-neutral-800 shadow-lg border border-neutral-200 dark:border-neutral-700 focus:outline-none z-10 py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={async () => {
                      const response = await api.get('/api/v1/appointments/export.ics', { responseType: 'blob' });
                      const url = URL.createObjectURL(new Blob([response.data], { type: 'text/calendar' }));
                      const a = document.createElement('a');
                      a.href = url; a.download = 'sage-appointments.ics'; a.click();
                      URL.revokeObjectURL(url);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                      active ? 'bg-neutral-100 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100' : 'text-neutral-700 dark:text-neutral-300'
                    }`}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Export .ics
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Menu>
          <button
            onClick={() => { setCreateDefaults(null); setIsCreateOpen(true); }}
            className="btn btn-primary"
          >
            <PlusIcon className="h-4 w-4" />
            New appointment
          </button>
        </div>
      </div>

      {/* ── Toolbar: navigation + view switcher ── */}
      <div className="flex items-center gap-3 mb-3 shrink-0 flex-wrap">
        {/* Prev / Today / Next */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => setAnchor((a) => navigate(view, a, 'prev'))}
            className="btn btn-secondary btn-icon"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => setAnchor(new Date())}
            className="btn btn-secondary btn-sm px-3"
          >
            Today
          </button>
          <button
            onClick={() => setAnchor((a) => navigate(view, a, 'next'))}
            className="btn btn-secondary btn-icon"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Range label */}
        <span className="font-medium text-neutral-800 dark:text-neutral-200 text-sm min-w-[200px]">
          {formatRangeLabel(view, anchor)}
        </span>

        {/* Density + view toggle */}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={toggleDensity}
            title={density === 'compact' ? 'Switch to default density' : 'Switch to compact density'}
            className={`btn btn-icon btn-secondary ${density === 'compact' ? 'ring-2 ring-primary-500 ring-offset-1 dark:ring-offset-neutral-900' : ''}`}
          >
            <ViewColumnsIcon className="h-4 w-4" />
          </button>
          <div className="flex items-center rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            {[
              { key: 'day', label: 'Day' },
              { key: 'workWeek', label: 'Work week' },
              { key: 'week', label: 'Week' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setView(key)}
                className={`px-3 py-1.5 text-sm border-r last:border-r-0 border-neutral-200 dark:border-neutral-700 transition-colors ${
                  view === key
                    ? 'bg-primary-600 text-white font-medium'
                    : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Provider filter legend ── */}
      {practitioners.length > 1 && (
        <div className="mb-3 shrink-0">
          <ProviderLegend
            practitioners={practitioners}
            practitionerColorMap={practitionerColorMap}
            selectedId={selectedPractitionerId}
            onSelect={setSelectedPractitionerId}
          />
        </div>
      )}

      {/* ── Pending requests panel ── */}
      <RequestsPanel
        requests={pendingRequests}
        onApprove={handleApprove}
        onDecline={handleDecline}
        approvingId={approvingId}
        decliningId={decliningId}
      />

      {/* ── Calendar grid ── */}
      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner />
        </div>
      ) : isError ? (
        <div className="text-center py-20 text-danger-600 dark:text-danger-400">Failed to load appointments.</div>
      ) : (
        <CalendarGrid
          days={days}
          appointments={appointments}
          externalEvents={externalEvents}
          onEdit={setEditingAppointment}
          onJoin={handleJoinTelehealth}
          onSlotClick={handleSlotClick}
          hourHeight={hourHeight}
          practitionerColorMap={practitionerColorMap}
          showPractitioner={showPractitioner}
        />
      )}

      {/* ── Create Modal ── */}
      <Modal isOpen={isCreateOpen} onClose={() => { setIsCreateOpen(false); setCreateDefaults(null); }} title="New Appointment" size="lg">
        <AppointmentForm
          defaultValues={createDefaults ?? {}}
          onSubmit={(formData) => createMutation.mutate(formData)}
          onCancel={() => { setIsCreateOpen(false); setCreateDefaults(null); }}
          isSubmitting={createMutation.isPending}
        />
      </Modal>

      {/* ── Edit Drawer ── */}
      <Drawer
        isOpen={!!editingAppointment}
        onClose={() => setEditingAppointment(null)}
        title="Edit Appointment"
        footer={
          editingAppointment && (
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                {!editingAppointment.isGroup && (
                  <Link
                    to={`/notes/new?clientId=${editingAppointment.clientId}&appointmentId=${editingAppointment.id}`}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Write note →
                  </Link>
                )}
                {!editingAppointment.isGroup && (
                  <Link
                    to={`/insurance/new?appointmentId=${editingAppointment.id}`}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Create claim →
                  </Link>
                )}
              </div>
              <div className="flex items-center gap-3">
                {editingAppointment.recurrenceGroupId && (
                  <button
                    onClick={() => cancelSeriesMutation.mutate({ id: editingAppointment.id })}
                    disabled={cancelSeriesMutation.isPending}
                    className="text-sm text-warning-600 hover:text-warning-700 disabled:opacity-60"
                  >
                    Cancel series
                  </button>
                )}
                <button
                  onClick={() => {
                    setDeleteTarget(editingAppointment);
                    setEditingAppointment(null);
                  }}
                  className="text-sm text-danger-600 hover:text-danger-700"
                >
                  Delete
                </button>
              </div>
            </div>
          )
        }
      >
        {editingAppointment && (
          <>
            <AppointmentForm
              key={editingAppointment.id}
              defaultValues={editDefaultValues}
              isEdit
              onSubmit={(formData) =>
                updateMutation.mutate({ id: editingAppointment.id, data: formData })
              }
              onCancel={() => setEditingAppointment(null)}
              isSubmitting={updateMutation.isPending}
            />
            {editingAppointment.isGroup && (
              <GroupMembersPanel
                appointment={editingAppointment}
                onRefresh={() => queryClient.invalidateQueries({ queryKey: ['appointments'] })}
              />
            )}
          </>
        )}
      </Drawer>

      {/* ── Telehealth Loading ── */}
      {telehealthLoading && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 flex items-center gap-3 shadow-modal">
            <Spinner />
            <span className="text-sm text-neutral-700 dark:text-neutral-300">Connecting to telehealth room…</span>
          </div>
        </div>
      )}

      {/* ── Telehealth Room ── */}
      <Modal
        isOpen={!!telehealthUrl}
        onClose={() => setTelehealthUrl(null)}
        title="Telehealth Session"
        size="xl"
      >
        <TelehealthRoom url={telehealthUrl} onClose={() => setTelehealthUrl(null)} />
      </Modal>

      {/* ── Delete Confirm ── */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete appointment?" size="sm">
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
          This will permanently delete the appointment. This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={() => setDeleteTarget(null)} className="btn btn-secondary">Cancel</button>
          <button
            onClick={() => deleteMutation.mutate(deleteTarget.id)}
            disabled={deleteMutation.isPending}
            className="btn btn-danger disabled:opacity-60"
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
